// Public homepage browser entry (bundled by esbuild into site/public/js/main.js; tasks
// 18.2-18.6). The server renders the full page (http/home.ts) and embeds the same state
// as <script type="application/json" id="initial-state">; this script keeps it live:
//
// - realtime: /ws through the shared client (lib/realtime.ts) with the "reconnecting"
//   indicator, 1 s -> 30 s backoff, GET /api/state polling every 10 s while
//   disconnected and a full resync after reconnecting (Req 2.4-2.9)
// - zones: POST /api/zones/{n}/toggle with the visitor CSRF token (Req 3, 4)
// - countdown: server clock offset, once-per-second text, refetch at zero (Req 10)
// - cameras: snapshot refresh of LIVE cameras only while visible, double-buffered,
//   solo view with Escape / back control and focus return (Req 11.12-11.17, 12)
// - laptop form: Turnstile token, 140 counter, open/closed switching (Req 13)
// - visitors + mood, stats line, latest comment, notices (Req 18, 19, 1.13, 1.14, 37.7)
//
// All pure logic lives in home/*.ts and lib/realtime.ts (unit-tested in Node).
import { CameraRefreshScheduler } from './home/cameraScheduler.js';
import { initFestiveToggle, safeLocalStorage } from './home/festive.js';
import {
  cameraLabel,
  cameraUrl,
  countdownExpired,
  countdownView,
  disabledReason,
  laptopCounterText,
  laptopOutcome,
  moodMessage,
  shouldSendToggle,
  statsText,
  STATS_UNAVAILABLE,
  toggleMessage,
  visitorsText,
  zoneView,
  type ControlContext,
} from './home/logic.js';
import {
  applyMessage,
  applySnapshot,
  emptyState,
  serverNow,
  type HomeState,
  type Part,
} from './home/state.js';
import { browserTimers, wsUrl } from './lib/browser.js';
import { RealtimeClient, type SocketLike } from './lib/realtime.js';

export function markScriptReady(root: HTMLElement = document.documentElement): void {
  root.classList.add('js');
}

interface TurnstileApi {
  reset(widget?: string | HTMLElement): void;
}

const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T | null =>
  root.querySelector<T>(sel);
const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T[] =>
  Array.from(root.querySelectorAll<T>(sel));

function setText(el: Element | null, text: string): void {
  if (el && el.textContent !== text) el.textContent = text;
}

function setHidden(el: HTMLElement | null, hidden: boolean): void {
  if (el && el.hidden !== hidden) el.hidden = hidden;
}

function csrfToken(): string {
  return $<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
}

async function postJson(url: string, body: unknown): Promise<{ status: number; body: unknown }> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', 'x-csrf-token': csrfToken() },
      body: JSON.stringify(body),
    });
    let parsed: unknown = null;
    try {
      parsed = await res.json();
    } catch {
      parsed = null;
    }
    return { status: res.status, body: parsed };
  } catch {
    return { status: 0, body: null };
  }
}

/** Shows `content` or the "currently unavailable" text of a section (Req 1.8). */
function sectionAvailable(section: HTMLElement | null, available: boolean): void {
  if (!section) return;
  setHidden($('[data-unavailable]', section), available);
  for (const c of $$('[data-content]', section)) setHidden(c, !available);
}

class HomePage {
  private readonly state: HomeState = emptyState();
  private readonly busyZones = new Set<number>();
  private readonly cameraLoads = new Map<number, boolean>();
  private loadCounter = 0;
  private soloCam: number | null = null;
  private lastExpiryFetch = 0;
  private realtime: RealtimeClient | null = null;
  private cameras: CameraRefreshScheduler | null = null;

  start(): void {
    const initial = $('#initial-state');
    if (initial?.textContent) {
      try {
        applySnapshot(this.state, JSON.parse(initial.textContent) as unknown, Date.now());
      } catch {
        // keep the server-rendered page as it is
      }
    }
    this.cameras = new CameraRefreshScheduler({
      timers: browserTimers,
      refresh: (n) => this.loadSnapshot(n),
      intervalS: this.state.cameraIntervalS,
      visible: document.visibilityState !== 'hidden',
    });
    this.wireZones();
    this.wireCameras();
    this.wireLaptop();
    this.render(
      new Set(['zones', 'countdown', 'cameras', 'visitors', 'stats', 'laptop', 'comment', 'notices']),
    );
    this.cameras.start();
    window.setInterval(() => this.tick(), 1000);
    document.addEventListener('visibilitychange', () => {
      const visible = document.visibilityState !== 'hidden';
      this.cameras?.setVisible(visible);
      // Req 12.4: fresh camera status (and everything else) on becoming visible.
      if (visible) void this.fetchState();
    });
    this.realtime = new RealtimeClient({
      url: wsUrl('/ws'),
      createSocket: (url) => new WebSocket(url) as unknown as SocketLike,
      timers: browserTimers,
      onMessage: (msg) => this.render(applyMessage(this.state, msg, Date.now())),
      onStatus: (s) => setHidden($('#live-status'), s !== 'reconnecting'),
      poll: () => this.fetchState(),
    });
    this.realtime.start();
  }

  /** GET /api/state; applies it on success, keeps the last values on failure (Req 2.9). */
  private async fetchState(): Promise<boolean> {
    try {
      const res = await fetch('/api/state', { credentials: 'same-origin', cache: 'no-store' });
      if (!res.ok) return false;
      const snap = (await res.json()) as unknown;
      this.render(applySnapshot(this.state, snap, Date.now()));
      return true;
    } catch {
      return false;
    }
  }

  private ctx(): ControlContext {
    return {
      controlsLive: this.state.controlsLive,
      reachable: this.state.reachable,
      showRunning: this.state.showRunning,
    };
  }

  private render(parts: Set<Part>): void {
    if (parts.has('zones')) this.renderZones();
    if (parts.has('countdown')) this.renderCountdown();
    if (parts.has('cameras')) this.renderCameras();
    if (parts.has('visitors') || parts.has('stats')) this.renderVisitors();
    if (parts.has('laptop')) this.renderLaptop();
    if (parts.has('comment')) this.renderComment();
    if (parts.has('notices')) this.renderNotices();
  }

  // --- zones ---------------------------------------------------------------------

  private renderZones(): void {
    const section = $('#zones');
    const zones = this.state.zones;
    sectionAvailable(section, zones !== null);
    const ctx = this.ctx();
    const reason = disabledReason(ctx);
    const notice = $('#zones-disabled');
    setText(notice, reason ?? '');
    setHidden(notice, reason === null);
    for (const z of zones ?? []) {
      const row = $('.zone-row[data-zone="' + String(z.n) + '"]');
      if (!row) continue;
      const v = zoneView(z, ctx);
      const busy = this.busyZones.has(z.n);
      row.dataset.state = v.label;
      row.dataset.requested = z.requested;
      setText($('.zone-name', row), z.name);
      setText($('.zone-desc', row), z.description);
      setText($('[data-state-label]', row), v.label);
      setHidden($('[data-pending]', row), !v.pending);
      setText($('[data-toggle-text]', row), v.toggleText);
      const toggle = $<HTMLButtonElement>('[data-action="toggle"]', row);
      const on = $<HTMLButtonElement>('[data-action="ON"]', row);
      const off = $<HTMLButtonElement>('[data-action="OFF"]', row);
      if (toggle) toggle.disabled = v.disabled || busy;
      if (on) on.disabled = !v.onEnabled || busy;
      if (off) off.disabled = !v.offEnabled || busy;
    }
  }

  private wireZones(): void {
    $('#zones')?.addEventListener('click', (ev) => {
      const btn = (ev.target as Element | null)?.closest<HTMLButtonElement>('button[data-action]');
      const row = btn?.closest<HTMLElement>('.zone-row');
      if (!btn || !row || btn.disabled) return;
      const n = Number(row.dataset.zone);
      const zone = this.state.zones?.find((z) => z.n === n);
      const action = btn.dataset.action as 'ON' | 'OFF' | 'toggle';
      if (!zone || !shouldSendToggle(action, zone.requested)) return;
      void this.toggle(n);
    });
  }

  private async toggle(n: number): Promise<void> {
    const message = $('#zones-message');
    this.busyZones.add(n);
    this.renderZones();
    const res = await postJson('/api/zones/' + String(n) + '/toggle', {});
    this.busyZones.delete(n);
    if (res.status === 200) {
      const b = res.body as { zone?: number; requested?: string } | null;
      const z = this.state.zones?.find((x) => x.n === n);
      if (z && (b?.requested === 'ON' || b?.requested === 'OFF')) {
        Object.assign(z, { requested: b.requested });
      }
      setText(message, '');
    } else {
      setText(message, toggleMessage(res.status, res.body));
    }
    this.renderZones();
  }

  // --- countdown -----------------------------------------------------------------

  private renderCountdown(): void {
    const section = $('#countdown');
    const c = this.state.countdown;
    sectionAvailable(section, c !== null);
    if (!section || !c) return;
    const v = countdownView(c, serverNow(this.state, Date.now()));
    section.dataset.status = v.status;
    setText($('.cd-status', section), v.status);
    setText($('[data-cd-label]', section), v.label);
    setText($('[data-cd-when]', section), v.when ?? '');
    setHidden($('.cd-time', section), v.time === null);
    setText($('[data-cd-time]', section), v.time ?? '');
    setText($('[data-cd-suffix]', section), v.status === 'ONLINE' ? 'left' : 'to go');
    // Only changes with status / label: assistive technologies hear it once (Req 10.7).
    setText($('#cd-announce'), v.announce);
  }

  private tick(): void {
    this.renderCountdown();
    const c = this.state.countdown;
    const now = Date.now();
    // Req 10.6: at zero, fetch the new state (the server pushes it too); at most every 2 s.
    if (c && countdownExpired(c, serverNow(this.state, now)) && now - this.lastExpiryFetch >= 2000) {
      this.lastExpiryFetch = now;
      void this.fetchState();
    }
  }

  // --- cameras -------------------------------------------------------------------

  private figure(n: number): HTMLElement | null {
    return $('.cam[data-cam="' + String(n) + '"]');
  }

  private renderCameras(): void {
    const section = $('#cameras');
    const cams = this.state.cameras;
    // The camera tiles (with placeholders) always render; only the notice switches.
    if (section) setHidden($('[data-unavailable]', section), cams !== null);
    this.cameras?.setIntervalS(this.state.cameraIntervalS);
    for (const c of cams ?? []) {
      const fig = this.figure(c.n);
      if (!fig) continue;
      const l = cameraLabel(c);
      const wasLive = fig.dataset.status === 'LIVE';
      fig.dataset.status = c.status;
      setText($('[data-cam-status]', fig), l.label);
      const reason = $('[data-cam-reason]', fig);
      setText(reason, l.reason);
      setHidden(reason, l.reason === '');
      if (c.status !== 'LIVE') {
        // Placeholder, no snapshot requests (Req 11.12, 11.17); solo view stays open (12.5).
        for (const img of $$<HTMLImageElement>('img', fig)) {
          const src = '/cams/offline-' + String(c.n) + '.jpg';
          if (!img.src.endsWith(src)) img.src = src;
          img.alt = c.name + ' offline';
        }
      } else if (!wasLive) {
        for (const img of $$<HTMLImageElement>('img', fig)) img.alt = c.name + ' snapshot';
      }
      this.cameras?.setStatus(c.n, c.status === 'LIVE');
    }
  }

  /** Loads a fresh snapshot into the hidden buffer and swaps it in when decoded. */
  private loadSnapshot(n: number): void {
    const fig = this.figure(n);
    const open = fig ? $('[data-cam-open]', fig) : null;
    if (!fig || !open || this.cameraLoads.get(n) === true) return;
    let front = $<HTMLImageElement>('img:not(.is-back)', open);
    let back = $<HTMLImageElement>('img.is-back', open);
    if (!front) return;
    if (!back) {
      back = front.cloneNode(false) as HTMLImageElement;
      back.classList.add('is-back');
      back.alt = '';
      back.setAttribute('aria-hidden', 'true');
      front.after(back);
    }
    this.cameraLoads.set(n, true);
    const done = (ok: boolean): void => {
      this.cameraLoads.set(n, false);
      if (!ok || fig.dataset.status !== 'LIVE' || !back || !front) return;
      back.classList.remove('is-back');
      back.alt = front.alt;
      back.removeAttribute('aria-hidden');
      front.classList.add('is-back');
      front.alt = '';
      front.setAttribute('aria-hidden', 'true');
      [front, back] = [back, front];
    };
    back.onload = () => done(true);
    back.onerror = () => done(false);
    this.loadCounter += 1;
    back.src = cameraUrl(n, this.loadCounter);
  }

  private wireCameras(): void {
    const grid = $('#cameras .cam-grid');
    if (!grid) return;
    grid.addEventListener('click', (ev) => {
      const target = ev.target as Element | null;
      const fig = target?.closest<HTMLElement>('.cam');
      if (!fig) return;
      const n = Number(fig.dataset.cam);
      if (target?.closest('[data-cam-back]')) {
        this.closeSolo();
      } else if (target?.closest('[data-cam-open]')) {
        if (this.soloCam === n) this.closeSolo();
        else this.openSolo(n);
      }
    });
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && this.soloCam !== null) {
        ev.preventDefault();
        this.closeSolo();
      }
    });
  }

  private openSolo(n: number): void {
    const grid = $('#cameras .cam-grid');
    const fig = this.figure(n);
    if (!grid || !fig) return;
    if (this.soloCam !== null) this.closeSolo(false);
    this.soloCam = n;
    grid.classList.add('solo');
    fig.classList.add('is-solo');
    const open = $('[data-cam-open]', fig);
    open?.setAttribute('aria-pressed', 'true');
    setText($('[data-cam-action]', fig), 'Back to all cameras');
    const back = $('[data-cam-back]', fig);
    setHidden(back, false);
    back?.focus();
  }

  private closeSolo(restoreFocus = true): void {
    const n = this.soloCam;
    if (n === null) return;
    this.soloCam = null;
    $('#cameras .cam-grid')?.classList.remove('solo');
    const fig = this.figure(n);
    if (!fig) return;
    fig.classList.remove('is-solo');
    const open = $('[data-cam-open]', fig);
    open?.setAttribute('aria-pressed', 'false');
    setText($('[data-cam-action]', fig), 'Show ' + ($('.cam-name', fig)?.textContent ?? 'camera') + ' alone');
    setHidden($('[data-cam-back]', fig), true);
    if (restoreFocus) open?.focus();
  }

  // --- laptop form ---------------------------------------------------------------

  private renderLaptop(): void {
    const open = this.state.laptopOpen;
    setHidden($('#laptop [data-unavailable]'), open !== null);
    // Hidden, not removed: typed text survives closing and reopening (Req 1.12).
    setHidden($('#laptop-form'), open !== true);
    setHidden($('#laptop-closed'), open !== false);
  }

  private wireLaptop(): void {
    const form = $<HTMLFormElement>('#laptop-form');
    const text = $<HTMLTextAreaElement>('#laptop-text');
    const counter = $('#laptop-count');
    const status = $('#laptop-status');
    if (!form || !text) return;
    const count = (): void => setText(counter, laptopCounterText(text.value));
    text.addEventListener('input', count);
    count();
    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      void (async () => {
        const token = $<HTMLInputElement>('[name="cf-turnstile-response"]', form)?.value ?? '';
        if (token === '') {
          setText(status, 'Please complete the check below the message first.');
          return;
        }
        const button = $<HTMLButtonElement>('button[type="submit"]', form);
        if (button) button.disabled = true;
        setText(status, 'Sending…');
        const res = await postJson('/api/laptop', { text: text.value, turnstileToken: token });
        const out = laptopOutcome(res.status, res.body);
        if (button) button.disabled = false;
        // Keep the text on every error (Req 13.3, 13.4); clear only once stored (13.11).
        if (out.clear) {
          text.value = '';
          count();
        }
        setText(status, out.message);
        // A Turnstile token is single-use: get a new one for the next try.
        (window as unknown as { turnstile?: TurnstileApi }).turnstile?.reset();
      })();
    });
  }

  // --- visitors, stats, comment, notices -----------------------------------------

  private renderVisitors(): void {
    const v = this.state.visitors;
    setHidden($('[data-visitors]'), v === null);
    setHidden($('#visitors-unavailable'), v !== null);
    if (v !== null) {
      setText($('#visitor-count'), visitorsText(v));
      setText($('#visitor-mood'), moodMessage(v));
    }
    setText(
      $('#stats-line'),
      statsText(this.state.togglesToday, this.state.overloadsToday) ?? STATS_UNAVAILABLE,
    );
  }

  private renderComment(): void {
    const section = $('#comment');
    const c = this.state.comment;
    if (!section) return;
    setHidden($('[data-unavailable]', section), c !== undefined);
    setHidden($('[data-comment]', section), !c);
    setHidden($('.c-none', section), c !== null);
    if (!c) return;
    setText($('.c-message', section), c.message);
    setText($('.c-name', section), c.name);
    const time = $<HTMLTimeElement>('.c-at', section);
    if (time) {
      time.dateTime = new Date(c.at).toISOString();
      setText(time, COMMENT_TIME.format(new Date(c.at)).replace(',', ''));
    }
  }

  private renderNotices(): void {
    setHidden($('#notice-autoplay'), !this.state.autoplayRunning);
    setHidden($('#notice-show'), !this.state.showRunning);
    setHidden($('#notice-link'), this.state.reachable !== false);
  }
}

const COMMENT_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

if (typeof document !== 'undefined') {
  markScriptReady();
  initFestiveToggle(
    document,
    safeLocalStorage(),
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  if (document.body?.classList.contains('home')) new HomePage().start();
}
